/**
 * Needs edit and the inbox, as the window sees them (library/needs_edit.py,
 * library/inbox.py in the engine).
 *
 * Both live in the engine, so every window and every machine on the library
 * sees the same flags and notices; this keeps one copy per window, re-read when
 * the engine says something changed.
 */

import { useEffect, useState } from "react";
import { clearNeedsEdit, dismissNotice, fetchInbox, fetchNeedsEdit, markNeedsEdit, type NeedsEditSong, type Notice } from "../api";
import { bus } from "./bus";
import { readPref } from "./prefs";
import { toast } from "../shell/Toast";
import { songs as countSongs } from "../format/count";

/** The reasons offered; anything else typed is kept as written. */
export const REASONS: { id: string; label: string; hint: string }[] = [
  { id: "dirty", label: "Dirty lyrics", hint: "Words that may not be played on air or in public." },
  { id: "tags", label: "Wrong tags", hint: "Title, artist, album, genre or year need correcting." },
  { id: "audio", label: "Audio problem", hint: "Clipping, a bad encode, a cut, silence." },
  { id: "cue points", label: "Cue points", hint: "The start, intro or mix-out need setting." },
  { id: "artwork", label: "Artwork", hint: "Missing or wrong cover." },
];

/**
 * What a plugin does about a reason, said where a song is flagged (Radio: "Radio
 * will not play it until it is marked fixed"). Returns the function that removes it.
 */
const notes = new Map<string, Set<string>>();
export function addReasonNote(reason: string, text: string): () => void {
  const set = notes.get(reason) ?? new Set<string>();
  set.add(text);
  notes.set(reason, set);
  return () => set.delete(text);
}
export const reasonNotes = (reason: string): string[] => [...(notes.get(reason) ?? [])];

export const reasonLabel = (reason: string) => REASONS.find((r) => r.id === reason)?.label ?? reason;

/** Who is flagging: Settings → General → Your name. */
export const USER_NAME_PREF = "nova.user.name";

export async function flagSongs(ids: string[], reason: string, note?: string): Promise<boolean> {
  const by = readPref<string>(USER_NAME_PREF, "").trim() || undefined;
  const r = await markNeedsEdit(ids, reason, note?.trim() || undefined, by);
  if (r.error) {
    toast(`Not flagged: ${r.error}`, "bad");
    return false;
  }
  toast(`${countSongs(ids.length)} flagged: ${reasonLabel(reason)}.`, "ok");
  return true;
}

export async function markFixed(ids: string[], title?: string): Promise<boolean> {
  const r = await clearNeedsEdit(ids);
  if (r.error) {
    toast(`Not marked fixed: ${r.error}`, "bad");
    return false;
  }
  toast(title ? `“${title}” marked fixed.` : `${countSongs(ids.length)} marked fixed.`, "ok");
  return true;
}

let flagged: NeedsEditSong[] = [];
let notices: Notice[] = [];
const listeners = new Set<() => void>();
let started = false;

async function refresh() {
  const [list, inbox] = await Promise.all([fetchNeedsEdit(), fetchInbox()]);
  flagged = list.songs;
  notices = inbox;
  listeners.forEach((fn) => fn());
}

function start() {
  if (started) return;
  started = true;
  let timer: number | null = null;
  const soon = () => {
    if (timer !== null) return;
    timer = window.setTimeout(() => {
      timer = null;
      void refresh();
    }, 300);
  };
  bus.on("needsEdit.changed", soon);
  bus.on("inbox.changed", soon);
  bus.on("engine.connected", soon);
  bus.on("engine.resync", soon);
  // A song renamed or removed changes how the list reads.
  bus.on("library.changed", soon);
  void refresh();
}

function useStore<T>(read: () => T): T {
  const [value, setValue] = useState(read);
  useEffect(() => {
    start();
    const fn = () => setValue(read());
    listeners.add(fn);
    fn();
    return () => {
      listeners.delete(fn);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return value;
}

/** Flagged songs, newest flag first. */
export const useNeedsEdit = () => useStore(() => flagged);
/** Open notices, newest first. */
export const useInbox = () => useStore(() => notices);
/** For the sidebar badge, outside React. */
export const flaggedCount = () => flagged.length;
export const dismiss = async (id: number) => {
  await dismissNotice(id);
  await refresh();
};
export const startNeedsEdit = start;
